import React, { useState, useRef, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, X } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'
import { useAuthStore } from '../../stores/authStore'
import { storage, auth } from '../../lib/firebase'
import { ref, listAll, uploadBytes, getDownloadURL } from 'firebase/storage'

interface DragDropZoneProps {
  sceneId: string | null
  projectId: string
  children: React.ReactNode
}

const DragDropZone: React.FC<DragDropZoneProps> = ({ sceneId, projectId, children }) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([])
  const { uploadImage } = useProjectStore()
  const { user } = useAuthStore()

  // Test Firebase Storage connection and configuration
  useEffect(() => {
    const testStorageConfiguration = async () => {
      try {
        console.log('🔧 Firebase Storage Configuration:')
        console.log('📦 Storage bucket:', storage.app.options.storageBucket)
        console.log('🆔 Project ID:', storage.app.options.projectId)
        console.log('👤 Auth state:', auth.currentUser ? '✅ Authenticated' : '❌ Not authenticated')
        console.log('🔑 User ID:', auth.currentUser?.uid || 'No user')
        
        if (auth.currentUser) {
          console.log('🎫 Auth token preview:')
          const token = await auth.currentUser.getIdToken()
          console.log('Token length:', token.length, 'characters')
          console.log('Token starts with:', token.substring(0, 20) + '...')
        }
        
        console.log('🧪 Testing Firebase Storage connection...')
        const testRef = ref(storage, 'test')
        await listAll(testRef)
        console.log('✅ Firebase Storage connection successful')
        
        // Test creating a reference
        const simpleRef = ref(storage, 'diagnostic-test')
        console.log('📁 Test reference created:', simpleRef.fullPath)
        console.log('🔗 Storage bucket from ref:', simpleRef.bucket)
        
      } catch (error) {
        console.error('❌ Firebase Storage configuration test failed:', error)
        if (error instanceof Error) {
          console.error('Error name:', error.name)
          console.error('Error message:', error.message)
          console.error('Error stack:', error.stack)
        }
      }
    }
    
    testStorageConfiguration()
  }, [])

  // Simple test upload function
  const testDirectUpload = async (file: File) => {
    try {
      console.log('🧪 Testing direct upload to Firebase Storage...')
      
      // Wait a moment to ensure auth is ready
      if (!auth.currentUser) {
        console.log('⏳ Waiting for authentication...')
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      
      console.log('👤 Current auth user:', auth.currentUser?.uid || 'Still not authenticated')
      
      // Create a simple storage reference
      const timestamp = Date.now()
      const storageRef = ref(storage, `test-uploads/${timestamp}-${file.name}`)
      
      console.log('📁 Storage path:', storageRef.fullPath)
      console.log('🪣 Storage bucket:', storageRef.bucket)
      console.log('📏 File size:', file.size, 'bytes')
      console.log('🎭 File type:', file.type)
      
      // Try to upload
      console.log('⬆️ Starting upload...')
      const snapshot = await uploadBytes(storageRef, file)
      console.log('✅ Direct upload successful!', snapshot.ref.fullPath)
      
      const url = await getDownloadURL(snapshot.ref)
      console.log('🔗 Download URL:', url)
      
      return url
    } catch (error) {
      console.error('❌ Direct upload failed:', error)
      if (error instanceof Error) {
        console.error('Error details:')
        console.error('- Name:', error.name)
        console.error('- Message:', error.message)
        console.error('- Stack:', error.stack)
      }
      throw error
    }
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (user) {
      setIsDragOver(true)
    }
  }, [user])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    if (!user) {
      console.error('User not authenticated')
      return
    }

    const files = Array.from(e.dataTransfer.files)
    const imageFiles = files.filter(file => file.type.startsWith('image/'))

    if (imageFiles.length === 0) {
      console.log('No image files found')
      return
    }

    console.log('🚀 Starting upload process for', imageFiles.length, 'files')
    console.log('👤 User:', user.uid)
    console.log('📋 Scene ID:', sceneId || 'No scene selected')
    console.log('📋 Project ID:', projectId)

    // Add files to uploading state
    const fileIds = imageFiles.map(file => `${file.name}-${Date.now()}`)
    setUploadingFiles(prev => [...prev, ...fileIds])

    try {
      // Try direct upload first (bypass complex logic)
      const uploadPromises = imageFiles.map(async (file, index) => {
        try {
          console.log(`🔄 Uploading file ${index + 1}/${imageFiles.length}:`, file.name)
          
          // Try direct upload first
          console.log('🧪 Attempting direct upload...')
          const directUrl = await testDirectUpload(file)
          console.log('✅ Direct upload successful for:', file.name)
          
          // If direct upload works but we have a scene, try the normal upload too
          if (sceneId) {
            console.log('🔄 Now trying normal upload through store...')
            await uploadImage(file, sceneId, projectId)
            console.log('✅ Store upload also successful for:', file.name)
          } else {
            console.log('⚠️ No scene selected, skipping store upload')
          }
          
        } catch (error) {
          console.error(`❌ Failed to upload ${file.name}:`, error)
        }
      })

      await Promise.all(uploadPromises)
    } catch (error) {
      console.error('❌ Upload batch failed:', error)
    } finally {
      // Remove files from uploading state
      setUploadingFiles(prev => prev.filter(id => !fileIds.includes(id)))
    }
  }, [projectId, uploadImage, user, sceneId])

  return (
    <div
      className="relative h-full"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}

      {/* Drag Overlay */}
      <AnimatePresence>
        {isDragOver && (
          <motion.div
            className="absolute inset-0 bg-primary-500/20 backdrop-blur-sm border-2 border-dashed border-primary-400 rounded-lg flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="text-center">
              <Upload className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <h3 className="text-2xl font-headline text-white mb-2">Drop Images Here</h3>
              <p className="font-body font-light text-white/80">
                {sceneId ? 'Release to upload your images to this scene' : 'Testing upload (no scene selected)'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Progress */}
      <AnimatePresence>
        {uploadingFiles.length > 0 && (
          <motion.div
            className="fixed bottom-4 right-4 glass-card p-4 z-50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
              <span className="font-ui font-normal text-white">
                Uploading {uploadingFiles.length} image{uploadingFiles.length !== 1 ? 's' : ''}...
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default DragDropZone 